import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { supabase } from '@/lib/supabase';
import { fmtMoeda } from '@/pages/admin/shared';

interface PlanoAdmin {
  id: string;
  nome: string;
  preco: number;
  descricao: string;
  condominios_max: number;
  usuarios_max: number;
  recursos: string[];
  destaque: boolean;
  popular: boolean;
  admin: number;
  sindico: number;
  usuarios_administrativos: number;
  moradores: number;
  visibilidade: 'ativo' | 'interno';
  stripe_product_id: string | null;
  stripe_price_id: string | null;
  data_criacao: string;
}

type FiltroVisibilidade = 'todos' | 'ativo' | 'interno';

interface PlanoFormData {
  id?: string;
  nome: string;
  preco: number;
  descricao: string;
  condominios_max: number;
  usuarios_max: number;
  recursos: string;
  admin: number;
  sindico: number;
  usuarios_administrativos: number;
  moradores: number;
  visibilidade: 'ativo' | 'interno';
}

const emptyForm: PlanoFormData = {
  nome: '',
  preco: 0,
  descricao: '',
  condominios_max: 1,
  usuarios_max: 1,
  recursos: '',
  admin: 1,
  sindico: 1,
  usuarios_administrativos: 0,
  moradores: 50,
  visibilidade: 'ativo',
};

export default function AdminPlanosPage() {
  const { isSuperuser } = useAuth();

  const [planos, setPlanos] = useState<PlanoAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filtro, setFiltro] = useState<FiltroVisibilidade>('todos');
  const [modalAberto, setModalAberto] = useState(false);
  const [planoEditando, setPlanoEditando] = useState<PlanoAdmin | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<PlanoAdmin | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchPlanos = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) {
        setError('Sessao expirada. Faca login novamente.');
        setLoading(false);
        return;
      }

      const { data, error: fnError } = await supabase.functions.invoke('admin-planos', {
        method: 'GET',
      });

      if (fnError) {
        if (fnError.message?.includes('Acesso restrito')) {
          setError('Acesso restrito a administradores.');
        } else {
          // Fallback: query DB directly
          const { data: dbData, error: dbError } = await supabase
            .from('planos')
            .select('*')
            .order('data_criacao', { ascending: false });

          if (dbError) throw new Error(dbError.message);
          setPlanos(dbData || []);
        }
        setLoading(false);
        return;
      }

      setPlanos(data?.planos || []);
    } catch (err: any) {
      // Fallback: query DB directly
      try {
        const { data: dbData } = await supabase
          .from('planos')
          .select('*')
          .order('data_criacao', { ascending: false });
        setPlanos(dbData || []);
      } catch {
        setError(err?.message || 'Erro ao carregar planos.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlanos();
  }, [fetchPlanos]);

  if (!isSuperuser) {
    return (
      <DashboardLayout>
        <div className="max-w-6xl flex flex-col items-center justify-center py-20">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
            <i className="ri-lock-line text-red-600 text-2xl"></i>
          </div>
          <h2 className="text-lg font-bold text-foreground-900 mb-2">Acesso Restrito</h2>
          <p className="text-sm text-foreground-500 text-center max-w-md">
            Esta area e reservada para o superusuario administrador da plataforma. Faca login com as
            credenciais de administrador para acessar.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  const planosFiltrados =
    filtro === 'todos' ? planos : planos.filter((p) => p.visibilidade === filtro);

  const totalAtivos = planos.filter((p) => p.visibilidade === 'ativo').length;
  const totalInternos = planos.filter((p) => p.visibilidade === 'interno').length;

  function abrirNovo() {
    setPlanoEditando(null);
    setModalAberto(true);
  }

  function abrirEdicao(plano: PlanoAdmin) {
    setPlanoEditando(plano);
    setModalAberto(true);
  }

  function fecharModal() {
    setModalAberto(false);
    setPlanoEditando(null);
  }

  async function handleSalvar(formData: PlanoFormData) {
    setSaving(true);
    try {
      const recursosArray = formData.recursos
        .split('\n')
        .map((r) => r.trim())
        .filter((r) => r.length > 0);

      const body = {
        ...formData,
        preco: Math.round(formData.preco * 100), // Converter reais para centavos
        recursos: recursosArray,
      };

      const isEditing = !!planoEditando;
      const method = isEditing ? 'PUT' : 'POST';
      const slug = isEditing ? `admin-planos/${planoEditando!.id}` : 'admin-planos';

      const { data, error: fnError } = await supabase.functions.invoke(slug, {
        method,
        body,
      });

      if (fnError) {
        // Fallback: try direct DB
        if (isEditing) {
          const { error: dbError } = await supabase
            .from('planos')
            .update({
              nome: body.nome,
              preco: body.preco,
              descricao: body.descricao,
              condominios_max: body.condominios_max,
              usuarios_max: body.usuarios_max,
              recursos: body.recursos,
              admin: body.admin,
              sindico: body.sindico,
              usuarios_administrativos: body.usuarios_administrativos,
              moradores: body.moradores,
              visibilidade: body.visibilidade,
            })
            .eq('id', planoEditando!.id);

          if (dbError) throw new Error(dbError.message);
        } else {
          const newId = `plano-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
          const { error: dbError } = await supabase.from('planos').insert({
            id: newId,
            nome: body.nome,
            preco: body.preco,
            descricao: body.descricao,
            condominios_max: body.condominios_max,
            usuarios_max: body.usuarios_max,
            recursos: body.recursos,
            admin: body.admin,
            sindico: body.sindico,
            usuarios_administrativos: body.usuarios_administrativos,
            moradores: body.moradores,
            visibilidade: body.visibilidade,
          });

          if (dbError) throw new Error(dbError.message);
        }

        showToast(
          isEditing
            ? 'Plano atualizado com sucesso (modo local).'
            : 'Plano criado com sucesso (modo local).',
          'success',
        );
      } else {
        showToast(
          isEditing
            ? 'Plano atualizado e sincronizado com Stripe.'
            : 'Plano criado e sincronizado com Stripe.',
          'success',
        );
      }

      fecharModal();
      await fetchPlanos();
    } catch (err: any) {
      showToast(err?.message || 'Erro ao salvar plano.', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleExcluir() {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      const { error: fnError } = await supabase.functions.invoke(
        `admin-planos/${confirmDelete.id}`,
        {
          method: 'DELETE',
        },
      );

      if (fnError) {
        // Fallback: delete directly from DB
        const { error: dbError } = await supabase
          .from('planos')
          .delete()
          .eq('id', confirmDelete.id);

        if (dbError) throw new Error(dbError.message);
        showToast('Plano removido (modo local).', 'success');
      } else {
        showToast('Plano removido e desativado no Stripe.', 'success');
      }

      setConfirmDelete(null);
      await fetchPlanos();
    } catch (err: any) {
      showToast(err?.message || 'Erro ao excluir plano.', 'error');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-xl md:text-2xl font-bold text-foreground-900">Planos</h1>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-accent-500 text-background-50">
                Super Admin
              </span>
            </div>
            <p className="text-sm text-foreground-500">
              Gerencie os planos, composicoes de usuarios e visibilidade para clientes.
            </p>
          </div>
          <button
            onClick={abrirNovo}
            className="whitespace-nowrap inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-add-line text-base"></i>
            Novo Plano
          </button>
        </div>

        {/* Cards de resumo */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center">
                <i className="ri-stack-line text-primary-600 text-sm"></i>
              </div>
              <p className="text-xs font-medium text-foreground-500">Total de Planos</p>
            </div>
            <p className="text-2xl font-bold text-foreground-900">{planos.length}</p>
            <p className="text-xs text-foreground-500 mt-1">Cadastrados na plataforma</p>
          </div>
          <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center">
                <i className="ri-eye-line text-green-600 text-sm"></i>
              </div>
              <p className="text-xs font-medium text-foreground-500">Planos Ativos</p>
            </div>
            <p className="text-2xl font-bold text-foreground-900">{totalAtivos}</p>
            <p className="text-xs text-foreground-500 mt-1">Visiveis para clientes</p>
          </div>
          <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                <i className="ri-lock-line text-amber-600 text-sm"></i>
              </div>
              <p className="text-xs font-medium text-foreground-500">Planos Internos</p>
            </div>
            <p className="text-2xl font-bold text-foreground-900">{totalInternos}</p>
            <p className="text-xs text-foreground-500 mt-1">Campanhas e acordos</p>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="bg-background-50 border border-background-200/70 rounded-lg p-10 flex items-center justify-center">
            <div className="text-center">
              <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
              <p className="text-sm text-foreground-500 mt-3">Carregando planos...</p>
            </div>
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="bg-background-50 border border-background-200/70 rounded-lg p-10 text-center">
            <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
              <i className="ri-error-warning-line text-2xl text-red-500"></i>
            </div>
            <p className="text-sm font-medium text-foreground-900 mb-2">Erro ao carregar planos</p>
            <p className="text-sm text-foreground-500 mb-4">{error}</p>
            <button
              onClick={fetchPlanos}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              Tentar novamente
            </button>
          </div>
        )}

        {/* Content */}
        {!loading && !error && (
          <>
            {/* Filtros */}
            <div className="flex items-center gap-2 mb-4">
              {(
                [
                  { key: 'todos', label: 'Todos', count: planos.length },
                  { key: 'ativo', label: 'Ativos', count: totalAtivos },
                  { key: 'interno', label: 'Internos', count: totalInternos },
                ] as { key: FiltroVisibilidade; label: string; count: number }[]
              ).map((f) => (
                <button
                  key={f.key}
                  onClick={() => setFiltro(f.key)}
                  className={`whitespace-nowrap px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer ${
                    filtro === f.key
                      ? 'bg-foreground-900 text-background-50'
                      : 'bg-background-50 text-foreground-600 border border-background-200/70 hover:bg-background-100'
                  }`}
                >
                  {f.label}
                  <span
                    className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] ${
                      filtro === f.key
                        ? 'bg-background-50/20 text-background-50'
                        : 'bg-background-100 text-foreground-500'
                    }`}
                  >
                    {f.count}
                  </span>
                </button>
              ))}
            </div>

            {/* Tabela */}
            <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
              {planosFiltrados.length === 0 ? (
                <div className="py-16 text-center">
                  <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-file-list-3-line text-foreground-400 text-lg"></i>
                  </div>
                  <p className="text-sm font-medium text-foreground-600">Nenhum plano encontrado</p>
                  <p className="text-xs text-foreground-400 mt-1">
                    Clique em "Novo Plano" para criar.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-background-200/70">
                        <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-500">
                          Plano
                        </th>
                        <th className="text-center px-4 py-3 text-xs font-semibold text-foreground-500">
                          Admin
                        </th>
                        <th className="text-center px-4 py-3 text-xs font-semibold text-foreground-500">
                          Sindico
                        </th>
                        <th className="text-center px-4 py-3 text-xs font-semibold text-foreground-500">
                          Usuarios Adm
                        </th>
                        <th className="text-center px-4 py-3 text-xs font-semibold text-foreground-500">
                          Moradores
                        </th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500">
                          Valor Total
                        </th>
                        <th className="text-center px-4 py-3 text-xs font-semibold text-foreground-500">
                          Visibilidade
                        </th>
                        <th className="text-center px-4 py-3 text-xs font-semibold text-foreground-500">
                          Stripe
                        </th>
                        <th className="text-center px-4 py-3 text-xs font-semibold text-foreground-500">
                          Acoes
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {planosFiltrados.map((plano) => (
                        <tr
                          key={plano.id}
                          className="border-b border-background-200/50 hover:bg-background-50/60 transition-colors"
                        >
                          <td className="px-5 py-3.5">
                            <p className="text-sm font-semibold text-foreground-900">
                              {plano.nome}
                            </p>
                            <p className="text-xs text-foreground-500 mt-0.5 line-clamp-1 max-w-[260px]">
                              {plano.descricao}
                            </p>
                          </td>
                          <td className="px-4 py-3.5 text-center">
                            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary-50 text-primary-700 text-xs font-bold">
                              {plano.admin}
                            </span>
                          </td>
                          <td className="px-4 py-3.5 text-center">
                            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-accent-50 text-accent-700 text-xs font-bold">
                              {plano.sindico}
                            </span>
                          </td>
                          <td className="px-4 py-3.5 text-center">
                            <span className="text-sm font-semibold text-foreground-900">
                              {plano.usuarios_administrativos}
                            </span>
                          </td>
                          <td className="px-4 py-3.5 text-center">
                            <span className="text-sm font-semibold text-foreground-900">
                              {plano.moradores >= 999 ? 'Ilimitado' : plano.moradores}
                            </span>
                          </td>
                          <td className="px-4 py-3.5 text-right">
                            <span className="text-sm font-bold text-foreground-900">
                              {fmtMoeda((plano.preco || 0) / 100)}
                            </span>
                          </td>
                          <td className="px-4 py-3.5 text-center">
                            <span
                              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
                                plano.visibilidade === 'ativo'
                                  ? 'bg-green-50 text-green-700 border border-green-200'
                                  : 'bg-amber-50 text-amber-700 border border-amber-200'
                              }`}
                            >
                              <i
                                className={`text-[10px] ${plano.visibilidade === 'ativo' ? 'ri-eye-line' : 'ri-lock-line'}`}
                              ></i>
                              {plano.visibilidade === 'ativo' ? 'Ativo' : 'Interno'}
                            </span>
                          </td>
                          <td className="px-4 py-3.5 text-center">
                            {plano.stripe_product_id ? (
                              <span className="inline-flex items-center gap-1 text-xs text-green-700">
                                <i className="ri-checkbox-circle-fill text-[10px]"></i>
                                Sincronizado
                              </span>
                            ) : (
                              <span className="text-xs text-foreground-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3.5">
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={() => abrirEdicao(plano)}
                                className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 hover:text-foreground-800 transition-colors cursor-pointer"
                                title="Editar plano"
                              >
                                <i className="ri-pencil-line text-sm"></i>
                              </button>
                              <button
                                onClick={() => setConfirmDelete(plano)}
                                className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-400 hover:bg-red-50 hover:text-red-600 transition-colors cursor-pointer"
                                title="Excluir plano"
                              >
                                <i className="ri-delete-bin-line text-sm"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* Modal de formulário */}
        {modalAberto && (
          <PlanoFormModal
            plano={planoEditando}
            onSalvar={handleSalvar}
            onFechar={fecharModal}
            saving={saving}
          />
        )}

        {/* Modal de confirmação de exclusão */}
        {confirmDelete && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
            onClick={() => setConfirmDelete(null)}
          >
            <div
              className="bg-background-50 rounded-lg shadow-lg w-full max-w-sm mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-error-warning-line text-red-600 text-xl"></i>
                </div>
                <h4 className="text-base font-bold text-foreground-900 mb-2">Excluir Plano</h4>
                <p className="text-sm text-foreground-500 mb-1">
                  Tem certeza que deseja excluir o plano
                </p>
                <p className="text-sm font-semibold text-foreground-900 mb-4">
                  &ldquo;{confirmDelete.nome}&rdquo;?
                </p>
                <p className="text-xs text-foreground-400 mb-5">
                  Esta acao nao pode ser desfeita. O produto tambem sera desativado no Stripe.
                </p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setConfirmDelete(null)}
                    disabled={deleting}
                    className="whitespace-nowrap flex-1 px-4 py-2.5 rounded-md text-sm font-semibold text-foreground-600 border border-background-200/70 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleExcluir}
                    disabled={deleting}
                    className="whitespace-nowrap flex-1 px-4 py-2.5 rounded-md text-sm font-semibold bg-red-600 text-white hover:bg-red-700 transition-colors cursor-pointer disabled:opacity-60"
                  >
                    {deleting ? 'Excluindo...' : 'Excluir'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Toast */}
        {toast && (
          <div className="fixed bottom-6 right-6 z-50">
            <div
              className={`px-4 py-3 rounded-lg text-sm font-medium shadow-lg flex items-center gap-2 ${
                toast.type === 'error'
                  ? 'bg-red-50 text-red-800 border border-red-200'
                  : 'bg-green-50 text-green-800 border border-green-200'
              }`}
            >
              <i
                className={
                  toast.type === 'error'
                    ? 'ri-error-warning-line text-red-600'
                    : 'ri-checkbox-circle-line text-green-600'
                }
              ></i>
              {toast.msg}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

/* ==================== Modal de Formulário ==================== */

function PlanoFormModal({
  plano,
  onSalvar,
  onFechar,
  saving,
}: {
  plano: PlanoAdmin | null;
  onSalvar: (dados: PlanoFormData) => void;
  onFechar: () => void;
  saving: boolean;
}) {
  const isEdicao = !!plano;

  const [form, setForm] = useState<PlanoFormData>({
    nome: plano?.nome ?? '',
    preco: plano ? (plano.preco || 0) / 100 : 0,
    descricao: plano?.descricao ?? '',
    condominios_max: plano?.condominios_max ?? 1,
    usuarios_max: plano?.usuarios_max ?? 1,
    recursos: plano?.recursos?.join('\n') ?? '',
    admin: plano?.admin ?? 1,
    sindico: plano?.sindico ?? 1,
    usuarios_administrativos: plano?.usuarios_administrativos ?? 0,
    moradores: plano?.moradores ?? 50,
    visibilidade: plano?.visibilidade ?? 'ativo',
  });

  const [erros, setErros] = useState<Record<string, string>>({});

  function validar(): boolean {
    const novosErros: Record<string, string> = {};
    if (!form.nome.trim()) novosErros.nome = 'Nome do plano e obrigatorio.';
    if (form.admin < 1) novosErros.admin = 'Minimo 1 admin.';
    if (form.sindico < 1) novosErros.sindico = 'Minimo 1 sindico.';
    if (form.usuarios_administrativos < 0)
      novosErros.usuariosAdministrativos = 'Nao pode ser negativo.';
    if (form.moradores < 1) novosErros.moradores = 'Minimo 1 morador.';
    if (form.preco <= 0) novosErros.preco = 'Valor deve ser maior que zero.';
    setErros(novosErros);
    return Object.keys(novosErros).length === 0;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validar()) return;
    onSalvar(form);
  }

  function setValorNumerico(campo: string, valor: string) {
    const num = valor === '' ? 0 : parseInt(valor, 10);
    if (!isNaN(num)) {
      setForm((prev) => ({ ...prev, [campo]: num }));
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onFechar}
    >
      <div
        className="bg-background-50 rounded-lg shadow-lg w-full max-w-xl mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-background-200/70">
          <h3 className="text-base font-bold text-foreground-900">
            {isEdicao ? 'Editar Plano' : 'Novo Plano'}
          </h3>
          <button
            onClick={onFechar}
            className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-400 hover:bg-background-100 hover:text-foreground-700 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-base"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 flex flex-col gap-5">
          {/* Nome */}
          <div>
            <label className="block text-sm font-semibold text-foreground-800 mb-1.5">
              Nome do Plano <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.nome}
              onChange={(e) => setForm((prev) => ({ ...prev, nome: e.target.value }))}
              className={`w-full px-3 py-2.5 rounded-md text-sm border bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 transition-colors ${
                erros.nome ? 'border-red-300' : 'border-background-200/70'
              }`}
              placeholder="Ex: Profissional, Essencial, Enterprise..."
            />
            {erros.nome && <p className="text-xs text-red-600 mt-1">{erros.nome}</p>}
          </div>

          {/* Descricao + Preco */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-foreground-800 mb-1.5">
                Valor Total (R$) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                min={0.01}
                value={form.preco || ''}
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  setForm((prev) => ({ ...prev, preco: isNaN(v) ? 0 : v }));
                }}
                className={`w-full px-3 py-2.5 rounded-md text-sm font-semibold border bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 transition-colors ${
                  erros.preco ? 'border-red-300' : 'border-background-200/70'
                }`}
                placeholder="0,00"
              />
              {erros.preco && <p className="text-xs text-red-600 mt-1">{erros.preco}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-foreground-800 mb-1.5">
                Visibilidade
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setForm((prev) => ({ ...prev, visibilidade: 'ativo' }))}
                  className={`whitespace-nowrap flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-md text-sm font-semibold border transition-colors cursor-pointer ${
                    form.visibilidade === 'ativo'
                      ? 'bg-green-50 text-green-700 border-green-300'
                      : 'bg-background-50 text-foreground-500 border-background-200/70 hover:bg-background-100'
                  }`}
                >
                  <i className="ri-eye-line text-sm"></i>
                  Ativo
                </button>
                <button
                  type="button"
                  onClick={() => setForm((prev) => ({ ...prev, visibilidade: 'interno' }))}
                  className={`whitespace-nowrap flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-md text-sm font-semibold border transition-colors cursor-pointer ${
                    form.visibilidade === 'interno'
                      ? 'bg-amber-50 text-amber-700 border-amber-300'
                      : 'bg-background-50 text-foreground-500 border-background-200/70 hover:bg-background-100'
                  }`}
                >
                  <i className="ri-lock-line text-sm"></i>
                  Interno
                </button>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-foreground-800 mb-1.5">
              Descricao
            </label>
            <textarea
              value={form.descricao}
              onChange={(e) => setForm((prev) => ({ ...prev, descricao: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2.5 rounded-md text-sm border border-background-200/70 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 transition-colors resize-none"
              placeholder="Breve descricao do plano..."
              maxLength={200}
            />
          </div>

          {/* Composicao de usuarios */}
          <div>
            <p className="text-sm font-semibold text-foreground-800 mb-3">Composicao de Usuarios</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">
                  Admin <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setForm((prev) => ({ ...prev, admin: Math.max(1, prev.admin - 1) }))
                    }
                    className="w-8 h-8 flex items-center justify-center rounded-md border border-background-200/70 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <i className="ri-subtract-line text-sm"></i>
                  </button>
                  <input
                    type="number"
                    min={1}
                    value={form.admin}
                    onChange={(e) => setValorNumerico('admin', e.target.value)}
                    className={`w-14 text-center px-2 py-2 rounded-md text-sm font-semibold border bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 transition-colors ${
                      erros.admin ? 'border-red-300' : 'border-background-200/70'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setForm((prev) => ({ ...prev, admin: prev.admin + 1 }))}
                    className="w-8 h-8 flex items-center justify-center rounded-md border border-background-200/70 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <i className="ri-add-line text-sm"></i>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">
                  Sindico <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setForm((prev) => ({ ...prev, sindico: Math.max(1, prev.sindico - 1) }))
                    }
                    className="w-8 h-8 flex items-center justify-center rounded-md border border-background-200/70 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <i className="ri-subtract-line text-sm"></i>
                  </button>
                  <input
                    type="number"
                    min={1}
                    value={form.sindico}
                    onChange={(e) => setValorNumerico('sindico', e.target.value)}
                    className="w-14 text-center px-2 py-2 rounded-md text-sm font-semibold border border-background-200/70 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 transition-colors"
                  />
                  <button
                    type="button"
                    onClick={() => setForm((prev) => ({ ...prev, sindico: prev.sindico + 1 }))}
                    className="w-8 h-8 flex items-center justify-center rounded-md border border-background-200/70 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <i className="ri-add-line text-sm"></i>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">
                  Usuarios Administrativos
                </label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        usuarios_administrativos: Math.max(0, prev.usuarios_administrativos - 1),
                      }))
                    }
                    className="w-8 h-8 flex items-center justify-center rounded-md border border-background-200/70 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <i className="ri-subtract-line text-sm"></i>
                  </button>
                  <input
                    type="number"
                    min={0}
                    value={form.usuarios_administrativos}
                    onChange={(e) => setValorNumerico('usuarios_administrativos', e.target.value)}
                    className="w-14 text-center px-2 py-2 rounded-md text-sm font-semibold border border-background-200/70 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 transition-colors"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        usuarios_administrativos: prev.usuarios_administrativos + 1,
                      }))
                    }
                    className="w-8 h-8 flex items-center justify-center rounded-md border border-background-200/70 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <i className="ri-add-line text-sm"></i>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">
                  Moradores <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setForm((prev) => ({ ...prev, moradores: Math.max(1, prev.moradores - 10) }))
                    }
                    className="w-8 h-8 flex items-center justify-center rounded-md border border-background-200/70 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <i className="ri-subtract-line text-sm"></i>
                  </button>
                  <input
                    type="number"
                    min={1}
                    value={form.moradores}
                    onChange={(e) => setValorNumerico('moradores', e.target.value)}
                    className="w-[72px] text-center px-2 py-2 rounded-md text-sm font-semibold border border-background-200/70 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 transition-colors"
                  />
                  <button
                    type="button"
                    onClick={() => setForm((prev) => ({ ...prev, moradores: prev.moradores + 10 }))}
                    className="w-8 h-8 flex items-center justify-center rounded-md border border-background-200/70 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <i className="ri-add-line text-sm"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Limites */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">
                Condominios Max
              </label>
              <input
                type="number"
                min={1}
                value={form.condominios_max}
                onChange={(e) => setValorNumerico('condominios_max', e.target.value)}
                className="w-full px-3 py-2.5 rounded-md text-sm border border-background-200/70 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 transition-colors"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">
                Usuarios Max
              </label>
              <input
                type="number"
                min={1}
                value={form.usuarios_max}
                onChange={(e) => setValorNumerico('usuarios_max', e.target.value)}
                className="w-full px-3 py-2.5 rounded-md text-sm border border-background-200/70 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 transition-colors"
              />
            </div>
          </div>

          {/* Recursos */}
          <div>
            <label className="block text-sm font-semibold text-foreground-800 mb-1.5">
              Recursos (um por linha)
            </label>
            <textarea
              value={form.recursos}
              onChange={(e) => setForm((prev) => ({ ...prev, recursos: e.target.value }))}
              rows={5}
              className="w-full px-3 py-2.5 rounded-md text-sm border border-background-200/70 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 transition-colors resize-none"
              placeholder="Dashboard com KPIs&#10;Gestao de ate 10 condominios&#10;Cadastro de torres e imoveis"
            />
          </div>

          {/* Resumo da composicao */}
          <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
            <p className="text-xs font-semibold text-foreground-500 mb-3 uppercase tracking-wide">
              Resumo da Composicao
            </p>
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-primary-50">
                <i className="ri-shield-user-line text-primary-600 text-sm"></i>
                <span className="text-xs font-medium text-primary-700">
                  <strong>{form.admin}</strong> Admin
                </span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-accent-50">
                <i className="ri-user-star-line text-accent-600 text-sm"></i>
                <span className="text-xs font-medium text-accent-700">
                  <strong>{form.sindico}</strong> Sindico
                </span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-secondary-50">
                <i className="ri-team-line text-secondary-700 text-sm"></i>
                <span className="text-xs font-medium text-secondary-900">
                  <strong>{form.usuarios_administrativos}</strong> Usuario
                  {form.usuarios_administrativos !== 1 ? 's' : ''} Adm
                </span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-background-100">
                <i className="ri-home-4-line text-foreground-600 text-sm"></i>
                <span className="text-xs font-medium text-foreground-700">
                  <strong>{form.moradores >= 999 ? 'Ilimitado' : form.moradores}</strong> Morador
                  {form.moradores !== 1 ? 'es' : ''}
                </span>
              </div>
            </div>
          </div>

          {/* Botoes */}
          <div className="flex items-center justify-end gap-3 pt-2 border-t border-background-200/70">
            <button
              type="button"
              onClick={onFechar}
              disabled={saving}
              className="whitespace-nowrap px-4 py-2.5 rounded-md text-sm font-semibold text-foreground-600 border border-background-200/70 hover:bg-background-100 transition-colors cursor-pointer"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="whitespace-nowrap px-5 py-2.5 rounded-md text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60 flex items-center gap-2"
            >
              {saving ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Salvando...
                </>
              ) : isEdicao ? (
                'Salvar Alteracoes'
              ) : (
                'Criar Plano'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
